<template>
  <div v-if="appConfig">
    <v-btn-toggle
      v-model="localVModel"
      :disabled="disabled"
      borderless
      class="lighten-2 elevation-0 ma-0 d-flex"
      dense
      mandatory
      style="width: 100%;"
    >
      <template v-for="item in visiblePaymentTypes">
        <v-btn
          :key="item.code"
          :class="[vModel === item.code ? 'flex-grow-1' : 'flex-grow-0', {'flex-grow-1': !shrink}]"
          :color="item?.color1"
          :disabled="disabled"
          :style="{height: `${buttonHeight}px`, border: '0', opacity: vModel === item.code ? '1' : '0.35'}"
          :value="item.code"
          class="elevation-0 white--text pa-2"
          min-width="42"
        >
          <span
            v-if="vModel === item.code && $vuetify.breakpoint.mdAndUp"
            class="mr-2"
          >
            {{ paymentTypeName(item) }}
          </span>

          <template v-if="!!item.image">
            <img
              :src="`${appConfig.LOCATION_DATA.CdnUrl}assets/payments/${item.image}`"
              :style="`filter: ${disabled ? 'grayscale(1)' : 'grayscale(0)'}`"
              height="22"
              width="auto"
            >
          </template>

          <template v-else>
            <v-icon
              v-if="item.icon"
              color="white"
              size="22"
            >
              {{ item.code === PaymentTypeEnum.CASH ? appCurrency.Icon : item.icon }}
            </v-icon>

            <v-icon
              v-if="item.icon2"
              color="white"
              size="22"
            >
              {{ item.icon2 }}
            </v-icon>
          </template>
        </v-btn>
      </template>

      <v-btn
        v-if="maxVisibleItems > -1"
        :class="[!selectedPaymentTypeIsVisible ? 'flex-grow-1' : 'flex-grow-0', {'flex-grow-1': !shrink}]"
        :color="selectedPaymentTypeIsVisible ? 'cyan' : selectedPaymentType?.color1"
        :disabled="disabled"
        :style="{height: `${buttonHeight}px`, border: '0', opacity: !selectedPaymentTypeIsVisible ? '1' : '0.35'}"
        class="elevation-0 white--text pa-2"
        min-width="42"
        value="handler"
        @click="showSelectPaymentMethodDialog"
      >
        <span
          v-if="!selectedPaymentTypeIsVisible && $vuetify.breakpoint.mdAndUp"
          class="mr-2"
        >
          {{ paymentTypeName(selectedPaymentType) }}
        </span>


        <template v-if="!selectedPaymentTypeIsVisible">
          <template v-if="!!selectedPaymentType.image">
            <img
              :src="`${appConfig.LOCATION_DATA.CdnUrl}assets/payments/${selectedPaymentType.image}`"
              :style="`filter: ${disabled ? 'grayscale(1)' : 'grayscale(0)'}`"
              height="22"
              width="auto"
            >
          </template>

          <template v-else>
            <v-icon
              v-if="selectedPaymentType.icon"
              color="white"
              size="22"
            >
              {{ selectedPaymentType.code === PaymentTypeEnum.CASH ? appCurrency.Icon : selectedPaymentType.icon }}
            </v-icon>

            <v-icon
              v-if="selectedPaymentType.icon2"
              color="white"
              size="22"
            >
              {{ selectedPaymentType.icon2 }}
            </v-icon>
          </template>
        </template>

        <template v-else>
          <v-icon
            color="white"
            size="22"
          >
            mdi-arrow-left
          </v-icon>
        </template>
      </v-btn>
    </v-btn-toggle>

    <select-payment-method-dialog
      v-model="vModel"
      :payment-methods="defaultPaymentTypesActive"
      :visible.sync="selectPaymentMethodDialogVisible"
      @select="onPaymentMethodSelected"
    />
  </div>
</template>

<script>
import AppData from '@/mixins/appdata.js'
import Auth from '@/mixins/auth.js'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum.js'
import PaymentType from '@/mixins/orders/paymentType.js'
import vModel from '@/mixins/vModel.js'
import SelectPaymentMethodDialog from '@/components/payments/SelectPaymentMethodDialog.vue'

const defaultButtonHeight = 36
const defaultVisibleItems = 2
export default {
  name      : 'PaymentMethodSelector',
  components: {SelectPaymentMethodDialog},
  mixins    : [AppData, Auth, PaymentType, vModel],
  props     : {
    paymentMethods: {
      type   : Array,
      default: undefined
    },
    height: {
      type   : [Number, String],
      default: defaultButtonHeight
    },
    maxVisibleItems: {
      type   : Number,
      default: defaultVisibleItems
    },
    disabled: {
      type   : Boolean,
      default: false
    },
    shrink: {
      type   : Boolean,
      default: false
    }
  },
  data() {
    return {
      selectPaymentMethodDialogVisible: false,
      localVModel                     : null
    }
  },
  computed: {
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },

    usePaymentTypes() {
      return this.paymentMethods || this.paymentTypesForPos || []
    },

    visiblePaymentTypes() {
      const paymentTypes = this.defaultPaymentTypesActive
      const maxVisibleItems = this.maxVisibleItems ?? defaultVisibleItems
      const visibleItems = maxVisibleItems > -1 ? maxVisibleItems : 1000
      return paymentTypes.slice(0, visibleItems)
    },

    selectedPaymentType() {
      return this.defaultPaymentTypesActive.find(paymentType => paymentType.code === this.vModel)
    },

    selectedPaymentTypeIsVisible() {
      return !!this.visiblePaymentTypes.find(paymentType => paymentType.code === this.vModel)
    },

    defaultPaymentTypesActive() {
      const paymentTypes = this.usePaymentTypes
      if (this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) return paymentTypes

      paymentTypes.push({
        name: 'MULTIPLE',
        code: PaymentTypeEnum.MULTIPLE,
        lang: {
          en: {
            Locale: 'en',
            Name  : 'Multiple Transactions'
          },
          el: {
            Locale: 'el',
            Name  : 'Πολλαπλές Συναλλαγές'
          }
        },
        icon          : 'euro_symbol',
        icon2         : 'credit_card',
        active        : true,
        color1        : 'purple darken-3',
        color2        : 'purple darken-4',
        priority      : 3,
        minOrderTotal : 0,
        orderStatus   : 11,
        orderStatusPos: 11
      })

      return paymentTypes
    },

    buttonHeight() {
      return parseInt(this.height ?? defaultButtonHeight)
    }
  },
  watch: {
    localVModel: {
      handler(newVal) {
        if (newVal !== 'handler') this.vModel = newVal
      },
      immediate: false
    },

    vModel: {
      handler(newVal) {
        this.localVModel = this.selectedPaymentTypeIsVisible ? newVal : 'handler'
      },
      immediate: false
    }
  },
  created() {
    this.localVModel = this.selectedPaymentTypeIsVisible ? this.vModel : 'handler'
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  methods: {
    paymentTypeName(paymentType) {
      let retVal = ''

      if (PaymentTypeEnum.isIris(paymentType?.code)) retVal = paymentType?.code?.toUpperCase()?.replaceAll('_', ' ') || this.getPaymentTypeLangByCode(paymentType?.code)?.Title || paymentType?.name || ''
      retVal = this.getPaymentTypeLangByCode(paymentType?.code)?.Title || paymentType?.name || ''

      return retVal.toLocaleUpperCase(this.$i18n.locale)
    },

    onPaymentMethodSelected(paymentMethod) {
      if (!paymentMethod) return

      this.vModel = paymentMethod.code
    },

    showSelectPaymentMethodDialog() {
      this.selectPaymentMethodDialogVisible = true
    }
  }
}
</script>

<style scoped>

</style>
